import { FormEvent, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Star, Trash2 } from 'lucide-react'
import { useAuth } from '../hooks/authContext'
import { useMyTestimonial, useSaveTestimonial, useDeleteTestimonial } from '../hooks/useApi'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'

export default function Testimonials() {
  const { user } = useAuth()
  const { data: mine, isLoading } = useMyTestimonial()
  const saveTestimonial = useSaveTestimonial()
  const deleteTestimonial = useDeleteTestimonial()

  const [displayName, setDisplayName] = useState('')
  const [businessName, setBusinessName] = useState('')
  const [content, setContent] = useState('')
  const [rating, setRating] = useState(5)

  useEffect(() => {
    if (mine) {
      setDisplayName(mine.displayName || '')
      setBusinessName(mine.businessName || '')
      setContent(mine.content || '')
      setRating(mine.rating || 5)
    } else if (user?.name) {
      setDisplayName(user.name)
    }
  }, [mine, user?.name])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    await saveTestimonial.mutateAsync({
      displayName: displayName.trim() || user?.name,
      businessName: businessName.trim() || undefined,
      content: content.trim(),
      rating,
    })
  }

  const handleDelete = async () => {
    if (!confirm('Hapus testimoni Anda dari halaman beranda?')) return
    await deleteTestimonial.mutateAsync()
    setContent('')
    setBusinessName('')
    setRating(5)
    if (user?.name) setDisplayName(user.name)
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Testimoni Saya</h1>
      <p className="text-gray-600 mb-8">
        Bagikan pengalaman Anda menggunakan UMKM Indonesia. Testimoni akan ditampilkan di halaman
        beranda setelah disimpan.
      </p>

      <Card>
        <CardHeader>
          <CardTitle>{mine ? 'Perbarui Testimoni' : 'Tulis Testimoni'}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-gray-500">Memuat...</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nama tampilan</label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Nama Anda"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nama usaha / brand (opsional)
                </label>
                <Input
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Contoh: Toko Sari Rasa"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      className="p-1 rounded hover:bg-amber-50"
                      aria-label={`${n} bintang`}
                    >
                      <Star
                        className={`h-7 w-7 ${n <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Isi testimoni</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  required
                  minLength={10}
                  rows={5}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ceritakan pengalaman Anda menggunakan platform ini..."
                />
                <p className="text-xs text-gray-500 mt-1">Minimal 10 karakter</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button type="submit" disabled={saveTestimonial.isPending}>
                  {saveTestimonial.isPending ? 'Menyimpan...' : mine ? 'Perbarui' : 'Kirim Testimoni'}
                </Button>
                {mine && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDelete}
                    disabled={deleteTestimonial.isPending}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Hapus
                  </Button>
                )}
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      <p className="mt-6 text-sm text-gray-500">
        Lihat testimoni di{' '}
        <Link to="/" className="text-indigo-600 hover:underline">
          halaman beranda
        </Link>
        .
      </p>
    </div>
  )
}
